import axios, { AxiosError } from 'axios';
import config from '@/config';
import { useAuthStore } from '@/stores/auth.store';
import { ensureSessionRefreshed } from '@/lib/auth';

const unauthenticatedClient = axios.create({
  baseURL: config.api.url,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const authenticatedClient = axios.create({
  baseURL: config.api.url,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

authenticatedClient.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();

    if (token) config.headers['Authorization'] = `Bearer ${token}`;

    return config;
  },

  (error) => {
    return Promise.reject(error);
  },
);

authenticatedClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const prevRequest = error.config;
    if (!prevRequest) return Promise.reject(error);
    prevRequest.attempt = prevRequest.attempt ? prevRequest.attempt + 1 : 1;

    // if the request was unauthorized (expired token or no token) and it wasn't sent before
    if ((error.response?.status === 401 || error.response?.status === 403) && prevRequest.attempt < 3) {
      try {
        await ensureSessionRefreshed();
        const { token } = useAuthStore.getState();

        prevRequest.headers.setAuthorization(`Bearer ${token}`);

        return authenticatedClient(prevRequest);
      } catch (error) {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export { unauthenticatedClient, authenticatedClient };
